import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  makeStyles
} from "@material-ui/core";
import CardSpacer from "@saleor/components/CardSpacer";
import { ConfirmButtonTransitionState } from "@saleor/components/ConfirmButton";
import Form from "@saleor/components/Form";
import SingleAutocompleteSelectField from "@saleor/components/SingleAutocompleteSelectField/SingleAutocompleteSelectField";
import { SingleSelectField } from "@saleor/components/SingleSelectField";
import { buttonMessages } from "@saleor/intl";
import { maybe } from "@saleor/misc";
import useScrollableDialogStyle from "@saleor/styles/useScrollableDialogStyle";
import { FetchMoreProps } from "@saleor/types";
import { CouponStatusEnum } from "@saleor/types/globalTypes";
import React, { useEffect, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";

export interface FormData {
  coupons?: any;
}

export interface AssignCouponDialogProps {
  fetchMoreCoupons: FetchMoreProps;
  open?: boolean;
  onClose?: () => void;
  onFetch: (value: string) => void;
  couponStatusQuery: CouponStatusEnum[];
  setCouponStatusQuery: (arr) => void;
  onSubmit?: (id: string) => void;
  confirmButtonState?: ConfirmButtonTransitionState;
  coupons?: any;
  loading?: boolean;
}

const useStyles = makeStyles(
  {
    confirmBtn: {
      "& .MuiButton-label": {
        color: "#FFFFFF"
      },
      "&:hover": {
        backgroundColor: "#ACCAFF"
      },
      "&:hover .MuiButton-label": {
        color: "#0030FF"
      },
      backgroundColor: "#0030FF"
    },
    contentDialog: {
      overflowY: "visible"
    },
    mainDialog: {
      "& .MuiDialog-paper": {
        height: "min-content",
        overflowY: "visible"
      }
    }
  },
  { name: "AssignCouponDialog" }
);

const AssignCouponDialog: React.FC<AssignCouponDialogProps> = props => {
  const {
    open,
    onClose,
    couponStatusQuery,
    setCouponStatusQuery,
    fetchMoreCoupons,
    loading,
    coupons,
    onSubmit,
    onFetch
  } = props;
  const scrollableDialogClasses = useScrollableDialogStyle({});
  const classes = useStyles(props);
  const intl = useIntl();
  const [couponID, setCouponID] = useState("");

  const filterDuplicates = (array, key) =>
    array.filter((item, index) => {
      const currentIndex = array.findIndex(obj => obj[key] === item[key]);
      return index === currentIndex;
    });

  const couponChoices = maybe(
    () =>
      filterDuplicates(coupons, "id").map(coupon => ({
        label: `${coupon.name}`,
        value: coupon.id
      })),
    []
  );

  const handleStatusChange = event => {
    const value = event.target.value;
    if (value === "All") {
      setCouponStatusQuery([
        CouponStatusEnum.ACTIVE,
        CouponStatusEnum.SCHEDULED
      ]);
    } else if (value === "Active") {
      setCouponStatusQuery([CouponStatusEnum.ACTIVE]);
    } else if (value === "Scheduled") {
      setCouponStatusQuery([CouponStatusEnum.SCHEDULED]);
    }
    setCouponID("");
  };

  const getCurrentStatus = () => {
    const isActive = couponStatusQuery.includes(CouponStatusEnum.ACTIVE);
    const isScheduled = couponStatusQuery.includes(CouponStatusEnum.SCHEDULED);
    if (isActive && !isScheduled) {
      return "Active";
    }
    if (!isActive && isScheduled) {
      return "Scheduled";
    }
    return "All";
  };
  const currentStatus = getCurrentStatus();

  const statusChoices = [
    {
      label: "All",
      value: "All"
    },
    {
      label: "Active",
      value: "Active"
    },
    {
      label: "Scheduled",
      value: "Scheduled"
    }
  ];

  const handleClose = () => {
    setCouponID("");
    setCouponStatusQuery([CouponStatusEnum.ACTIVE, CouponStatusEnum.SCHEDULED]);
    onClose();
  };

  const handleSubmit = () => {
    onSubmit(couponID);
    handleClose();
  };

  useEffect(() => {
    if (couponID === "") {
      setCouponID("");
    }
  }, [couponID]);

  return (
    <Form onSubmit={handleSubmit} confirmLeave>
      {({ submit }) => (
        <Dialog
          onClose={handleClose}
          open={open}
          classes={{ paper: scrollableDialogClasses.dialog }}
          className={classes.mainDialog}
          fullWidth
          maxWidth="sm"
        >
          <DialogTitle>
            <FormattedMessage
              defaultMessage="Assign Coupon"
              description="dialog header"
            />
          </DialogTitle>
          <DialogContent className={classes.contentDialog}>
            <SingleSelectField
              label={intl.formatMessage({
                defaultMessage: "Coupon Status"
              })}
              name="couponStatus"
              disabled={loading}
              value={currentStatus}
              choices={statusChoices}
              onChange={handleStatusChange}
              lowerDropdown={true}
            />
            <CardSpacer />
            <SingleAutocompleteSelectField
              idInputTxt="searchCouponTxt"
              label={intl.formatMessage({
                defaultMessage: "Coupon Title"
              })}
              name="couponTitle"
              displayValue={couponID}
              disabled={loading}
              value={couponID}
              fetchChoices={onFetch}
              choices={couponChoices}
              onChange={e => {
                setCouponID(e.target.value);
              }}
              {...fetchMoreCoupons}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={onClose}>
              <FormattedMessage {...buttonMessages.back} />
            </Button>
            <Button
              className={classes.confirmBtn}
              id="assignCouponBtn"
              variant="contained"
              type="submit"
              onClick={submit}
              disabled={loading || couponID === ""}
            >
              <FormattedMessage
                defaultMessage="Assign coupon"
                description="button"
              />
            </Button>
          </DialogActions>
        </Dialog>
      )}
    </Form>
  );
};
AssignCouponDialog.displayName = "AssignCouponDialog";
export default AssignCouponDialog;
